import React from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import { propsToDom, propsToLink } from 'core/helpers';
import { ContainerGrid } from 'components/Container';
import { Panel } from 'components/Panel';
import { Image } from 'components/Image';
import { Link } from 'components/Link';
import { useGlobalContext } from 'store/GlobalProvider';
import { smallCSS, Title6 } from 'styles/typography';
import { RichText } from 'components/RichText';
import { rem } from 'styles/utils';
import { transitionFillHighlight } from 'styles/transitions';
import media from 'styles/media';
import { useSplitting } from 'hooks/useSplitting';
import { splittedRevealCSS } from 'styles/others';
import { Reveal } from 'components/Reveal';

const ThumbnailFullComponent = ({ id, primary, items, slice_type, next_slice_type, ...others }) => {
  const { isMobile } = useGlobalContext();
  const $title = useSplitting();
  const { background, image_on_right, main_title, text, link, image } = primary;
  const propsImage = isMobile ? image?.thumbnails?.mobile : image;
  const propsLink = link?.url ? propsToLink(link) : {};
  const hasImage = !!propsImage?.url;
  const hasTitle = !!main_title?.text;
  const hasText = !!text?.richText?.length > 0;
  const hasLink = !!propsLink?.to;

  return (
    <Panel
      {...propsToDom(others)}
      id={slice_type}
      nextId={next_slice_type}
      background={background}
      data-link={hasLink}
    >
      <ContainerGrid>
        <ImageContainer as={hasLink ? Link : null} {...propsLink} isImageRight={image_on_right}>
          {hasImage && <Image {...propsImage} />}
        </ImageContainer>
        <Content isImageRight={image_on_right}>
          {hasTitle && (
            <Title6 ref={$title} as={hasLink ? Link : 'h2'} {...propsLink}>
              {main_title.text}
            </Title6>
          )}
          {hasText && (
            <Reveal stagger={2}>
              <RichText render={text.richText} />
            </Reveal>
          )}
        </Content>
      </ContainerGrid>
    </Panel>
  );
};

const Content = styled.div`
  grid-row: 1 / span 1;
  grid-column-end: span 2;
  grid-column-start: ${({ isImageRight }) => (isImageRight ? 1 : 'auto')};

  ${Title6} {
    ${splittedRevealCSS};
  }

  ${RichText} {
    ${smallCSS};
    margin-top: ${rem(8)};
    opacity: 0.5;
    color: inherit;
  }

  ${media.mobile`
    grid-row: 2 / span 1;
    grid-column: 1 / -1;
  `}
`;

const ImageContainer = styled.div`
  grid-row: 1 / span 1;
  grid-column-end: span 6;
  grid-column-start: ${({ isImageRight }) => (isImageRight ? 3 : 'auto')};
  margin-left: ${({ isImageRight }) => (isImageRight ? null : `calc(var(--margin) * -1)`)};
  margin-right: ${({ isImageRight }) => (isImageRight ? `calc(var(--margin) * -1)` : null)};
  border-radius: ${({ isImageRight }) => (isImageRight ? `8px 0 0 8px` : `0 8px 8px 0`)};
  overflow: hidden;

  ${Image} {
    position: relative;
    transition: transform 0.7s ${({ theme }) => theme.easings.principle};
  }

  ${media.mobile`
    grid-row: 1 / span 1;
    grid-column: 1 / -1;
    margin-left: calc(var(--margin) * -1);
    margin-right: calc(var(--margin) * -1);
    border-radius: 0;

    ${Image} {
      margin-bottom: ${rem(24)};
    }
  `}
`;

export const ThumbnailFull = styled(ThumbnailFullComponent)`
  &[data-link='true'] {
    pointer-events: none;

    a,
    button {
      pointer-events: auto;
    }

    ${Title6} {
      ${transitionFillHighlight};
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        ${Image} {
          transform: scale(1.05);
        }

        ${Title6} {
          color: ${({ theme }) => theme.colors.accent};
        }
      }
    }
  }

  ${ContainerGrid} {
    align-items: center;
  }
`;

export const query = graphql`
  fragment SliceThumbnailFullHome on PrismicHomeDataBodyThumbnailFull {
    id
    slice_type
    primary {
      background
      image_on_right
      main_title {
        text
      }
      text {
        richText
      }
      link {
        url
        target
      }
      image {
        url
        alt
        dimensions {
          width
          height
        }
        thumbnails {
          mobile {
            url
            alt
            dimensions {
              width
              height
            }
          }
        }
      }
    }
  }

  fragment SliceThumbnailFullPage on PrismicPageDataBodyThumbnailFull {
    id
    slice_type
    primary {
      background
      image_on_right
      main_title {
        text
      }
      text {
        richText
      }
      link {
        url
        target
      }
      image {
        url
        alt
        dimensions {
          width
          height
        }
        thumbnails {
          mobile {
            url
            alt
            dimensions {
              width
              height
            }
          }
        }
      }
    }
  }
`;
